import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Pinterest as PinterestIcon,
  Share as ShareIcon,
  VideoLibrary as VideoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

// Platform icons mapping
const platformIcons = {
  instagram: <InstagramIcon sx={{ color: '#E4405F' }} />,
  linkedin: <LinkedInIcon sx={{ color: '#0A66C2' }} />,
  youtube: <YouTubeIcon sx={{ color: '#FF0000' }} />,
  twitter: <TwitterIcon sx={{ color: '#1DA1F2' }} />,
  pinterest: <PinterestIcon sx={{ color: '#E60023' }} />,
  tiktok: <ShareIcon sx={{ color: '#000000' }} />
};

const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'easy': return 'success';
    case 'medium': return 'warning';
    case 'hard': return 'error';
    case 'expensive': return 'secondary';
    default: return 'default';
  }
};

const SocialMediaRegistration = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Register dialog
  const [registerDialog, setRegisterDialog] = useState({ open: false, platform: null });
  const [formData, setFormData] = useState({
    account_name: '',
    account_handle: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    refresh_token: '',
    bearer_token: '',
    notes: ''
  });

  // Seed dialog for admin
  const [seedDialog, setSeedDialog] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/social-media/platforms');
      setPlatforms(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch platforms');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedPlatforms = async () => {
    try {
      setSeeding(true);
      await api.post('/social-media/platforms/seed');
      setSuccess('Default platforms seeded successfully');
      setSeedDialog(false);
      fetchPlatforms();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to seed platforms');
    } finally {
      setSeeding(false);
    }
  };

  const handleRegister = async () => {
    try {
      await api.post('/social-media/accounts', {
        platform_id: registerDialog.platform.id,
        ...formData
      });
      setSuccess(`${registerDialog.platform.name} account registered successfully`);
      handleCloseRegister();
      navigate('/digital-media/my-accounts');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to register account');
    }
  };

  const handleOpenRegister = (platform) => {
    setRegisterDialog({ open: true, platform });
    setFormData({
      account_name: '',
      account_handle: '',
      api_key: '',
      api_secret: '',
      access_token: '',
      refresh_token: '',
      bearer_token: '',
      notes: ''
    });
  };

  const handleCloseRegister = () => {
    setRegisterDialog({ open: false, platform: null });
    setFormData({
      account_name: '',
      account_handle: '',
      api_key: '',
      api_secret: '',
      access_token: '',
      refresh_token: '',
      bearer_token: '',
      notes: ''
    });
  };

  const getPlatformIcon = (platformCode) => {
    return platformIcons[platformCode?.toLowerCase()] || <ShareIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Social Media Registration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Register your social media accounts to enable posting and scheduling
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isAdmin && platforms.length === 0 && (
            <Button
              variant="outlined"
              onClick={() => setSeedDialog(true)}
            >
              Initialize Platforms
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => navigate('/digital-media/my-accounts')}
          >
            View My Accounts
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {platforms.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ShareIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No Platforms Available</Typography>
          <Typography color="text.secondary" gutterBottom>
            {isAdmin 
              ? 'Click "Initialize Platforms" to add default social media platforms.'
              : 'Please contact an administrator to set up social media platforms.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {platforms.map((platform) => (
            <Grid item xs={12} sm={6} md={4} key={platform.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: 4,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ fontSize: 40 }}>
                      {getPlatformIcon(platform.platform_code)}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {platform.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {platform.platform_code?.toUpperCase()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip 
                      label={platform.difficulty} 
                      size="small" 
                      color={getDifficultyColor(platform.difficulty)}
                    />
                    {platform.supports_video && (
                      <Chip 
                        icon={<VideoIcon />} 
                        label={platform.video_format || 'Video'} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Best for:</strong> {platform.best_use_case}
                  </Typography>

                  {platform.monthly_cost > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <MoneyIcon fontSize="small" color="warning" />
                      <Typography variant="body2" color="warning.main">
                        ~${platform.monthly_cost}/month API cost
                      </Typography>
                    </Box>
                  )}

                  {platform.setup_notes && (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <InfoIcon fontSize="small" color="info" />
                        <Typography variant="caption" fontWeight={600}>Setup Notes</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {platform.setup_notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <Divider />

                <CardActions sx={{ p: 2 }}>
                  <Button 
                    fullWidth 
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenRegister(platform)}
                  >
                    Register Account
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Register Account Dialog */}
      <Dialog open={registerDialog.open} onClose={handleCloseRegister} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {registerDialog.platform && getPlatformIcon(registerDialog.platform.platform_code)}
            Register {registerDialog.platform?.name} Account
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              API credentials are encrypted before storage for security.
            </Alert>

            <TextField
              label="Account Name"
              required
              fullWidth
              value={formData.account_name}
              onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
              placeholder="e.g., My Business Account"
            />

            <TextField
              label="Account Handle / Username"
              fullWidth
              value={formData.account_handle}
              onChange={(e) => setFormData({ ...formData, account_handle: e.target.value })}
              placeholder="e.g., @mycompany"
            />

            <Divider sx={{ my: 1 }}>
              <Chip label="API Credentials" size="small" />
            </Divider>

            <TextField
              label="API Key"
              fullWidth
              value={formData.api_key}
              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
              placeholder="Your API Key"
            />

            <TextField
              label="API Secret"
              fullWidth
              type="password"
              value={formData.api_secret}
              onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
              placeholder="Your API Secret"
            />

            <TextField
              label="Access Token"
              fullWidth
              type="password"
              value={formData.access_token}
              onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
              placeholder="Your Access Token"
            />

            <TextField
              label="Refresh Token"
              fullWidth
              type="password"
              value={formData.refresh_token}
              onChange={(e) => setFormData({ ...formData, refresh_token: e.target.value })}
              placeholder="Your Refresh Token (if applicable)"
            />

            <TextField
              label="Bearer Token"
              fullWidth
              type="password"
              value={formData.bearer_token}
              onChange={(e) => setFormData({ ...formData, bearer_token: e.target.value })}
              placeholder="Your Bearer Token (if applicable)"
            />

            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes about this account"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegister}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleRegister}
            disabled={!formData.account_name}
          >
            Register Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seed Platforms Dialog */}
      <Dialog open={seedDialog} onClose={() => setSeedDialog(false)}>
        <DialogTitle>Initialize Default Platforms</DialogTitle>
        <DialogContent>
          <Typography>
            This will add the following default social media platforms:
          </Typography>
          <Box sx={{ mt: 2 }}>
            <ul>
              <li>Instagram (Medium setup, Reels support)</li>
              <li>LinkedIn (Easy setup, Professional/B2B)</li>
              <li>TikTok (Hard setup - audit required, Short-form video)</li>
              <li>X/Twitter (Expensive - $200+/month API)</li>
              <li>YouTube (Medium setup, Shorts support)</li>
              <li>Pinterest (Easy setup, Shopping/Inspiration)</li>
            </ul>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSeedDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSeedPlatforms}
            disabled={seeding}
          >
            {seeding ? <CircularProgress size={20} /> : 'Initialize'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialMediaRegistration;
